/* eslint camelcase: "off" */
import React from 'react';
import {
  Tag,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@jem-open/jem-ui';

import {
  Operator,
  PolicyRequirement,
  RequirementGroup,
  RequirementsBlock,
} from '../../../../types/VerificationTypes';
import { isFlatCompatible } from './requirementsBlockHelpers';


type OverrideMode = 'additive' | 'replace';
type ChipVariant = 'neutral' | 'outline';


const SectionHint: React.FC<{ label: string; hint?: React.ReactNode }> = ({ label, hint }) => (
  <div className="mb-xs flex items-baseline gap-xxs">
    <span className="text-xs font-semibold uppercase tracking-wide text-greyscale-text-caption">
      {label}
    </span>
    {hint && (
      <>
        <span className="text-xs text-greyscale-text-caption">·</span>
        <span className="text-xs italic text-greyscale-text-caption">{hint}</span>
      </>
    )}
  </div>
);


interface RequirementChipProps {
  code: string;
  variant?: ChipVariant;
  requirementsByCode?: Map<string, PolicyRequirement>;
  satisfiedByCode?: Map<string, PolicyRequirement[]>;
}

/**
 * One requirement chip. When the registry maps are supplied, the chip
 * becomes hover-able and the tooltip stacks any of:
 *   * Remediation flow (for remediable requirements)
 *   * "Also satisfied by" — the inverse of `satisfies`, computed from the
 *     registry (held stronger credentials that subsume this one).
 *   * "Also satisfies" — the requirement's own `satisfies` list (weaker
 *     credentials this one subsumes).
 *
 * No tooltip is rendered when there's nothing to add (keeps non-hover
 * chips visually identical to before).
 */
const RequirementChip: React.FC<RequirementChipProps> = ({
  code,
  variant = 'neutral',
  requirementsByCode,
  satisfiedByCode,
}) => {
  const requirement = requirementsByCode?.get(code);
  const remediationFlow =
    requirement?.is_remediable && requirement.remediation_flow
      ? requirement.remediation_flow
      : null;
  const satisfies = requirement?.satisfies ?? [];
  const satisfiedBy = satisfiedByCode?.get(code) ?? [];

  const hasTooltip =
    remediationFlow != null || satisfies.length > 0 || satisfiedBy.length > 0;
  if (!hasTooltip) {
    return <Tag variant={variant}>{code}</Tag>;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-block cursor-help">
          <Tag variant={variant}>{code}</Tag>
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <div className="flex flex-col gap-xxs text-xs">
          {remediationFlow && (
            <div>
              Remediation flow: <code className="font-mono">{remediationFlow}</code>
            </div>
          )}
          {satisfiedBy.length > 0 && (
            <div>
              Also satisfied by:{' '}
              {satisfiedBy.map((r, idx) => (
                <React.Fragment key={r.code}>
                  {idx > 0 && ', '}
                  <code className="font-mono">{r.code}</code>
                </React.Fragment>
              ))}
            </div>
          )}
          {satisfies.length > 0 && (
            <div>
              Also satisfies:{' '}
              {satisfies.map((c, idx) => (
                <React.Fragment key={c}>
                  {idx > 0 && ', '}
                  <code className="font-mono">{c}</code>
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
};


interface RequirementsBlockViewProps {
  label?: string;
  value: RequirementsBlock | Record<string, never>;
  helpText?: string;
  emptyHint?: string;
  /**
   * Implicit operator for the section. When the value collapses to a single
   * group matching this operator, the view renders as a flat chip list and
   * skips the "Group N" framing. AND for required, OR for step-up.
   */
  defaultOperator?: Operator;
  /**
   * When provided alongside `overrideMode`, render `value` as a diff against
   * this base block. Used on the employer-override policy detail page so the
   * reader sees the effective policy, not just the override's own delta.
   */
  baseValue?: RequirementsBlock | Record<string, never>;
  overrideMode?: OverrideMode;
  /**
   * Registry of PolicyRequirement objects keyed by code. When provided in
   * the plain (non-diff) flat case, the view renders each requirement as a
   * row (icon + code + description) instead of a chip, splitting the
   * Required block into "Hard" + "Remediable" sub-sections. Step-up rows
   * also pick up their TTL badge from here. Has no effect in diff mode or
   * grouped mode (those keep the chip layout).
   */
  requirementsByCode?: Map<string, PolicyRequirement>;
  /**
   * Inverse of `PolicyRequirement.satisfies` — for each code, the
   * requirements (held elsewhere) that would also satisfy a policy asking
   * for it. Drives the "also satisfied by" hint on the chip tooltips.
   * Build once at the page level via `buildSatisfiedByMap`.
   */
  satisfiedByCode?: Map<string, PolicyRequirement[]>;
  /**
   * Marks this block as the step-up section so rows render with the clock
   * icon + TTL badge instead of the hard/remediable split.
   */
  isStepUp?: boolean;
}




function getFlatRequirements(
  value: RequirementsBlock | Record<string, never>
): { code: string }[] {
  const groups = (value as RequirementsBlock).groups || [];
  return groups[0]?.requirements || [];
}


/**
 * Read-only render of a requirements block. Mirrors the editor: flat chip
 * list for the common single-implicit-operator case; grouped cards only when
 * the value actually uses conditional grouping.
 *
 * In override-diff mode (`baseValue` + `overrideMode`), chips are styled to
 * show which come from the base and which from the override:
 *   - additive: base chips neutral, override-added chips pink
 *   - replace: base chips struck through in a "Replaced — no longer applies"
 *     row, override chips pink in a "Now applies" row
 *
 * The diff only renders when both sides are flat. When either uses
 * conditional grouping, falls back to showing base + override stacked.
 */
const RequirementsBlockView: React.FC<RequirementsBlockViewProps> = ({
  label,
  value,
  helpText,
  emptyHint = 'No requirements.',
  defaultOperator = 'AND',
  baseValue,
  overrideMode,
  requirementsByCode,
  satisfiedByCode,
  isStepUp,
}) => {
  const header = (
    <>
      {label && (
        <h6 className="mb-xxs text-base font-semibold text-greyscale-text-title">{label}</h6>
      )}
      {helpText && (
        <p className="mb-xs text-xs text-greyscale-text-caption">{helpText}</p>
      )}
    </>
  );

  // Override-diff mode — render value as a comparison against baseValue.
  if (baseValue !== undefined && overrideMode) {
    const valueFlat = isFlatCompatible(value, defaultOperator);
    const baseFlat = isFlatCompatible(baseValue, defaultOperator);

    if (valueFlat && baseFlat) {
      return (
        <div>
          {header}
          <DiffFlat
            base={getFlatRequirements(baseValue)}
            override={getFlatRequirements(value)}
            overrideMode={overrideMode}
            defaultOperator={defaultOperator}
            emptyHint={emptyHint}
            requirementsByCode={requirementsByCode}
            satisfiedByCode={satisfiedByCode}
          />
        </div>
      );
    }

    // Conditional grouping in use on either side — fall back to side-by-side.
    return (
      <div>
        {header}
        <p className="m-none mb-xs text-xs italic text-greyscale-text-caption">
          Conditional grouping in use — showing base and override side by side.
        </p>
        <div className="mb-sm">
          <p className="m-none mb-xxs text-xs font-semibold uppercase tracking-wide text-greyscale-text-caption">
            Global default
          </p>
          <PlainBlock
            value={baseValue}
            defaultOperator={defaultOperator}
            emptyHint={emptyHint}
            requirementsByCode={requirementsByCode}
            satisfiedByCode={satisfiedByCode}
          />
        </div>
        <div>
          <p className="m-none mb-xxs text-xs font-semibold uppercase tracking-wide text-greyscale-text-caption">
            This override
          </p>
          <PlainBlock
            value={value}
            defaultOperator={defaultOperator}
            emptyHint={emptyHint}
            requirementsByCode={requirementsByCode}
            satisfiedByCode={satisfiedByCode}
          />
        </div>
      </div>
    );
  }

  // Plain (non-diff) render.
  return (
    <div>
      {header}
      <PlainBlock
        value={value}
        defaultOperator={defaultOperator}
        emptyHint={emptyHint}
        requirementsByCode={requirementsByCode}
        satisfiedByCode={satisfiedByCode}
        isStepUp={isStepUp}
      />
    </div>
  );
};


// ---------------------------------------------------------------------------
// Plain (non-diff) block render — used by the non-override case and by the
// fallback when conditional grouping defeats chip-level diffing.
// ---------------------------------------------------------------------------

interface PlainBlockProps {
  value: RequirementsBlock | Record<string, never>;
  defaultOperator: Operator;
  emptyHint: string;
  requirementsByCode?: Map<string, PolicyRequirement>;
  satisfiedByCode?: Map<string, PolicyRequirement[]>;
  isStepUp?: boolean;
}

const PlainBlock: React.FC<PlainBlockProps> = ({
  value,
  defaultOperator,
  emptyHint,
  requirementsByCode,
  satisfiedByCode,
  isStepUp,
}) => {
  const groups: RequirementGroup[] = (value as RequirementsBlock).groups || [];
  const flat = isFlatCompatible(value, defaultOperator);
  const flatRequirements = flat ? (groups[0]?.requirements || []) : [];

  if (groups.length === 0) {
    return <p className="m-none text-sm italic text-greyscale-text-caption">{emptyHint}</p>;
  }

  if (flat && requirementsByCode && !isStepUp) {
    // Required: chip list, split into Hard + Remediable sub-sections with
    // SectionHint headers (label left, italic hint right).
    const hard = flatRequirements.filter(
      (r) => !requirementsByCode.get(r.code)?.is_remediable
    );
    const remediable = flatRequirements.filter(
      (r) => requirementsByCode.get(r.code)?.is_remediable
    );

    return (
      <TooltipProvider delayDuration={150}>
        <div className="flex flex-col gap-md">
          {hard.length > 0 && (
            <div>
              <SectionHint label="Hard requirements" hint="deny if missing" />
              <div className="flex flex-wrap gap-xxs">
                {hard.map((r) => (
                  <RequirementChip
                    key={r.code}
                    code={r.code}
                    requirementsByCode={requirementsByCode}
                    satisfiedByCode={satisfiedByCode}
                  />
                ))}
              </div>
            </div>
          )}
          {remediable.length > 0 && (
            <div>
              <SectionHint label="Remediable" hint="chatbot can capture in-band" />
              <div className="flex flex-wrap gap-xxs">
                {remediable.map((r) => (
                  <RequirementChip
                    key={r.code}
                    code={r.code}
                    requirementsByCode={requirementsByCode}
                    satisfiedByCode={satisfiedByCode}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </TooltipProvider>
    );
  }

  // Step-up or no-registry fallback: single chip list.
  if (flat) {
    const flatIntro = defaultOperator === 'AND'
      ? 'All of the following must be held.'
      : 'Any of the following must be fresh.';
    return (
      <TooltipProvider delayDuration={150}>
        <div>
          <p className="m-none mb-xs text-xs italic text-greyscale-text-caption">{flatIntro}</p>
          <div className="flex flex-wrap gap-xxs">
            {flatRequirements.map((r) => (
              <RequirementChip
                key={r.code}
                code={r.code}
                requirementsByCode={requirementsByCode}
                satisfiedByCode={satisfiedByCode}
              />
            ))}
          </div>
        </div>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider delayDuration={150}>
      <div className="flex flex-col gap-sm">
        {groups.map((g, idx) => (
          <div
            key={g.group_id}
            className="rounded-xl border border-greyscale-border-default bg-greyscale-surface-subtle p-sm"
          >
            <div className="mb-xs flex items-center gap-xs">
              <Tag variant="default">Group {idx + 1}</Tag>
              {idx > 0 && (
                <span className="text-xs text-greyscale-text-caption">AND</span>
              )}
              <span className="text-xs font-semibold text-greyscale-text-body">
                {g.operator}
              </span>
            </div>
            <div className="flex flex-wrap gap-xxs">
              {g.requirements.map((r) => (
                <RequirementChip
                  key={`${g.group_id}-${r.code}`}
                  code={r.code}
                  requirementsByCode={requirementsByCode}
                  satisfiedByCode={satisfiedByCode}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </TooltipProvider>
  );
};


// ---------------------------------------------------------------------------
// Diff render (flat case) — additive uses one mixed row; replace uses two
// labelled rows (replaced + applies).
// ---------------------------------------------------------------------------

interface DiffFlatProps {
  base: { code: string }[];
  override: { code: string }[];
  overrideMode: OverrideMode;
  defaultOperator: Operator;
  emptyHint: string;
  requirementsByCode?: Map<string, PolicyRequirement>;
  satisfiedByCode?: Map<string, PolicyRequirement[]>;
}

const DiffFlat: React.FC<DiffFlatProps> = ({
  base,
  override,
  overrideMode,
  defaultOperator,
  emptyHint,
  requirementsByCode,
  satisfiedByCode,
}) => {
  const overrideCodes = new Set(override.map((r) => r.code));

  const flatIntro = defaultOperator === 'AND'
    ? 'All of the following must be held.'
    : 'Any of the following must be fresh.';

  if (overrideMode === 'additive') {
    // Inherit base; override layers on top. Each code is shown once under
    // its true category — codes present in both the default and the
    // override are *inherited* (the override is just re-affirming them);
    // only codes absent from the default are flagged as added by the
    // override. This avoids misleading admins into thinking a default
    // requirement is new to the override.
    const baseCodes = new Set(base.map((r) => r.code));
    const inherited = base;
    const overrideAdded = override.filter((r) => !baseCodes.has(r.code));

    if (inherited.length === 0 && overrideAdded.length === 0) {
      return <p className="m-none text-sm italic text-greyscale-text-caption">{emptyHint}</p>;
    }

    return (
      <TooltipProvider delayDuration={150}>
        <div>
          <p className="m-none mb-xs text-xs italic text-greyscale-text-caption">{flatIntro}</p>
          <div className="flex flex-wrap gap-xxs">
            {inherited.map((r) => (
              <RequirementChip
                key={r.code}
                code={r.code}
                variant="neutral"
                requirementsByCode={requirementsByCode}
                satisfiedByCode={satisfiedByCode}
              />
            ))}
            {overrideAdded.map((r) => (
              <RequirementChip
                key={r.code}
                code={r.code}
                variant="outline"
                requirementsByCode={requirementsByCode}
                satisfiedByCode={satisfiedByCode}
              />
            ))}
          </div>
          <p className="m-none mt-xs text-xs text-greyscale-text-caption">
            <span className="inline-block h-[8px] w-[8px] rounded-full bg-greyscale-border-default align-middle" />{' '}
            Inherited from global default
            <span className="mx-xs">·</span>
            <span className="inline-block h-[8px] w-[8px] rounded-full border border-greyscale-text-body align-middle" />{' '}
            Added by this override
          </p>
        </div>
      </TooltipProvider>
    );
  }

  // Replace mode — base no longer applies; override is the policy.
  const replaced = base.filter((r) => !overrideCodes.has(r.code));

  return (
    <TooltipProvider delayDuration={150}>
      <div>
        {replaced.length > 0 && (
          <div className="mb-sm">
            <p className="m-none mb-xxs text-xs italic text-greyscale-text-caption">
              Replaced — these no longer apply:
            </p>
            <div className="flex flex-wrap gap-xxs">
              {replaced.map((r) => (
                <span
                  key={r.code}
                  className="inline-block opacity-60"
                  style={{ textDecoration: 'line-through' }}
                >
                  <Tag variant="neutral">{r.code}</Tag>
                </span>
              ))}
            </div>
          </div>
        )}
        <div>
          <p className="m-none mb-xxs text-xs italic text-greyscale-text-caption">
            {override.length === 0 ? 'Now applies:' : `${flatIntro} Now applies:`}
          </p>
          {override.length === 0 ? (
            <p className="m-none text-sm italic text-greyscale-text-caption">
              No requirements — this override leaves the action ungated.
            </p>
          ) : (
            <div className="flex flex-wrap gap-xxs">
              {override.map((r) => (
                <RequirementChip
                  key={r.code}
                  code={r.code}
                  variant="outline"
                  requirementsByCode={requirementsByCode}
                  satisfiedByCode={satisfiedByCode}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
};


export default RequirementsBlockView;
